import "@/i18n";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Sparkles, MapPin, Calendar, LogOut } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useFormatPrice } from "@/lib/currency";

export const Route = createFileRoute("/account")({
  head: () => ({
    meta: [{ title: "My account — Bacha Way Travel" }],
  }),
  component: AccountPage,
});

interface BookingRow {
  id: string;
  created_at: string;
  pickup: string;
  dropoff: string;
  travel_date: string;
  travel_time: string;
  status: string;
  price_mad: number;
  vehicle_type: string;
}

function AccountPage() {
  const { t } = useTranslation();
  const { user, isAuthenticated, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const fmt = useFormatPrice();
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate({ to: "/login", replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      const { data } = await supabase
        .from("bookings")
        .select("id, created_at, pickup, dropoff, travel_date, travel_time, status, price_mad, vehicle_type")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (!cancelled) {
        setBookings((data as BookingRow[]) ?? []);
        setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (loading || !user) {
    return <div className="mx-auto max-w-3xl px-6 py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div>;
  }

  const name = (user.user_metadata?.full_name as string) || user.email || "";

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary">{t("auth.myAccount")}</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl">{name}</h1>
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            <Sparkles className="h-3 w-3" /> {t("auth.discountActive")}
          </div>
        </div>
        <button onClick={() => signOut()} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-muted">
          <LogOut className="h-4 w-4" /> {t("auth.signOut")}
        </button>
      </div>

      <h2 className="font-display text-2xl mb-4">{t("auth.myBookings")}</h2>

      {busy ? (
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      ) : bookings.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center">
          <p className="text-muted-foreground mb-4">{t("auth.noBookings")}</p>
          <Link to="/booking" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90">
            {t("nav.book")}
          </Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {bookings.map((b) => (
            <li key={b.id} className="rounded-xl border border-border bg-card p-4 sm:p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <MapPin className="h-4 w-4 text-primary" />
                    <span className="truncate">{b.pickup}</span>
                    <span className="text-muted-foreground">→</span>
                    <span className="truncate">{b.dropoff}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {b.travel_date} · {b.travel_time}</span>
                    <span className="capitalize">{b.vehicle_type}</span>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${b.status === "confirmed" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>{b.status}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg text-primary whitespace-nowrap">{fmt(b.price_mad)}</div>
                  <Link to="/booking/status/$id" params={{ id: b.id }} className="text-xs text-primary hover:underline">
                    {t("auth.viewBooking")}
                  </Link>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
